import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import CustomModal from '../pages/common/CustomModal';
import CustomButton from './CustomButton';
import SingleDatePicker from './ui/SingleDatePicker';

interface Props {
    open: boolean;
    /** `YYYY-MM-DD`, or empty for an open end. */
    dateFrom: string;
    dateTo: string;
    onDateFromChange: (date: string) => void;
    onDateToChange: (date: string) => void;
    /** Shown while neither date is set — says what an unbounded export contains. */
    allPeriodsHint: string;
    onCancel: () => void;
    onExport: () => void;
}

/**
 * The "From / To" step in front of a CSV export. Shared by the bookings and
 * problem-report exports so both ask the same question the same way.
 */
const CsvDateRangeDialog = ({
    open,
    dateFrom,
    dateTo,
    onDateFromChange,
    onDateToChange,
    allPeriodsHint,
    onCancel,
    onExport,
}: Props) => {
    const { t } = useTranslation();

    return (
        <CustomModal
            open={open}
            title={t('csv_export.dialog_title')}
            onClose={onCancel}
            closeButton={<CustomButton title={t('common.cancel')} isFilled={false} onClick={onCancel} />}
            saveButton={<CustomButton title={t('csv_export.export_button')} onClick={onExport} />}
        >
            <div className={'flex flex-col gap-4'}>
                <SingleDatePicker
                    label={t('csv_export.date_from')}
                    value={dateFrom ? dayjs(dateFrom) : null}
                    onChange={(date) => onDateFromChange(date ? date.format('YYYY-MM-DD') : '')}
                />
                <SingleDatePicker
                    label={t('csv_export.date_to')}
                    value={dateTo ? dayjs(dateTo) : null}
                    onChange={(date) => onDateToChange(date ? date.format('YYYY-MM-DD') : '')}
                />
                {!dateFrom && !dateTo && (
                    <p className={'text-sm text-foreground/60'}>{allPeriodsHint}</p>
                )}
            </div>
        </CustomModal>
    );
};

export default CsvDateRangeDialog;
